import React from 'react';
import { EMAIL_SLIDE, PAUSE_SLIDE, PLAN_GRAPH_SLIDE, PLAN_LOADER_SLIDE, PRODUCT_SLIDE, QUESTION_ANSWER_SLIDE_COMPONENT, QUESTION_MULTI_ANSWER_SLIDE_COMPONENT, START_SLIDE } from '../quiz-component-types';
import pauseCollage1Src from '../../assets/images/slide/pause-collage-1-dad.png';
import pauseCollage2Src from '../../assets/images/slide/pause-collage-2.png';
import pauseCollage3Src from '../../assets/images/slide/pause-collage-3.png';
import startImageDadSrc from '../../assets/images/slide/start-image-memory-dad.png';

const PLAN_TOPIC_ANSWERS = [
    {
        text: 'Childhood',
        icon: 'house',
        id: 1
    },
    {
        text: 'School years',
        icon: 'backpack',
        id: 2
    },
    {
        text: 'The twenties',
        icon: 'bottle',
        id: 3
    },
    {
        text: 'Love and marriage',
        icon: 'groom',
        id: 4
    },
    {
        text: 'Raising you',
        icon: 'new-father',
        id: 5
    },
    {
        text: 'The working years',
        icon: 'dad-working',
        id: 6
    },
    {
        text: 'These days',
        icon: 'tree',
        id: 7
    }
];

const QUIZ_SLIDES_V28 = [
    {
        id: 900,
        component: START_SLIDE,
        isNotIndexing: true,
        props: {
            imageSrc: startImageDadSrc,
            heading: <><strong>Your dad’s</strong> whole <strong>life story</strong>, saved in one place</>,
            subheading: "Take the 2-minute quiz to personalize the subscription we'll build for him",
            ageQuestion: {
                id: 901,
                category: 'Him & you',
                question: 'How old is he?',
                label: 'Start by selecting his age',
                answers: [
                    {
                        text: '80+',
                        id: 7
                    },
                    {
                        text: '70–79',
                        id: 6
                    },
                    {
                        text: '60–69',
                        id: 5
                    },
                    {
                        text: 'Under 60',
                        id: 4
                    }
                ]
            }
        }
    },
    {
        id: 902,
        component: QUESTION_ANSWER_SLIDE_COMPONENT,
        props: {
            category: 'Him & you',
            question: 'How often are you two together?',
            answers: [
                {
                    text: 'Most weeks',
                    id: 1
                },
                {
                    text: 'Once a month or so',
                    id: 2
                },
                {
                    text: 'A few times a year',
                    id: 3
                },
                {
                    text: 'Hardly ever',
                    id: 4
                }
            ]
        }
    },
    {
        id: 903,
        component: QUESTION_ANSWER_SLIDE_COMPONENT,
        props: {
            category: 'Him & you',
            question: 'And when you do — what do you talk about?',
            answers: [
                {
                    text: 'Health, weather, routine tasks',
                    id: 4
                },
                {
                    text: "Sports, news, or what's on TV",
                    id: 5
                },
                {
                    text: 'The same 3 stories told again',
                    id: 6
                },
                {
                    text: 'Honestly — not much at all',
                    id: 7
                }
            ]
        }
    },
    {
        id: 9031,
        component: PAUSE_SLIDE,
        isNotIndexing: true,
        continueButton: {
            isVisible: true
        },
        props: {
            imageSrc: pauseCollage1Src,
            titlePrefix: 'You talk about everything.',
            titleAccent: 'Except him.',
            description: <>You know how he is this week. That's a different thing from <strong>knowing who he was.</strong> There's a whole person in there you've never been introduced to.</>
        }
    },
    {
        id: 904,
        component: QUESTION_MULTI_ANSWER_SLIDE_COMPONENT,
        continueButton: {
            isVisible: true
        },
        props: {
            category: 'Him & you',
            question: 'Which of these do you actually know?',
            description: 'Be honest. Most people tick one or two.',
            answers: [
                {
                    text: 'Who he wanted to be, before you',
                    icon: 'cap',
                    id: 1
                },
                {
                    text: "Whether he ever doubted he'd be a good dad",
                    icon: 'self-doubt',
                    id: 2
                },
                {
                    text: 'The hardest year he’s ever had',
                    icon: 'wave',
                    id: 3
                },
                {
                    text: 'The pressure he carried to provide — and whether it ever let up',
                    icon: 'ribbon',
                    id: 4
                },
                {
                    text: "What he's proud of that has nothing to do with you",
                    icon: 'trophy',
                    id: 5
                },
                {
                    text: 'Honestly — none of these',
                    icon: 'sad',
                    id: 6
                }
            ]
        }
    },
    {
        id: 9041,
        component: PAUSE_SLIDE,
        isNotIndexing: true,
        continueButton: {
            isVisible: true
        },
        props: {
            imageSrc: pauseCollage2Src,
            titlePrefix: 'It’s not because you',
            titleAccent: 'don’t love him.',
            description: <>{'We tell ourselves we’ll ask ‘someday’. But ‘someday’ is a lie. '}<strong>One day, there are no more tomorrows</strong>{' — only silence and regret for questions left unasked forever.'}</>
        }
    },
    {
        id: 905,
        component: QUESTION_ANSWER_SLIDE_COMPONENT,
        props: {
            category: 'Him & you',
            question: 'What happens when you try to ask about his past?',
            answers: [
                {
                    text: '"Oh, nobody wants to hear about my life"',
                    icon: 'monkey',
                    id: 4
                },
                {
                    text: 'He gives you the facts, but skips the feelings',
                    icon: 'refresh',
                    id: 5
                },
                {
                    text: 'He tells the same safe story he always tells',
                    icon: 'hourglass',
                    id: 6
                },
                {
                    text: 'He starts to open up, but time runs out',
                    icon: 'blossom',
                    id: 7
                },
                {
                    text: 'Honestly — I’ve never brought it up',
                    icon: 'neutral',
                    id: 8
                }
            ]
        }
    },
    {
        id: 906,
        component: QUESTION_ANSWER_SLIDE_COMPONENT,
        props: {
            category: 'Him & you',
            question: 'Now think about his mother. Your grandmother.',
            description: 'What do you know?',
            answers: [
                {
                    text: 'A lot — I knew her well',
                    icon: 'heart',
                    id: 1
                },
                {
                    text: 'Two or three stories',
                    icon: 'puzzle',
                    id: 2
                },
                {
                    text: 'Her name and a face',
                    icon: 'worried',
                    id: 3
                },
                {
                    text: 'Almost nothing',
                    icon: 'question',
                    id: 4
                }
            ]
        }
    },
    {
        id: 9061,
        component: PAUSE_SLIDE,
        isNotIndexing: true,
        continueButton: {
            isVisible: true
        },
        props: {
            imageSrc: pauseCollage3Src,
            titlePrefix: 'That’s how',
            titleAccent: 'fast it goes',
            isTitleInline: true,
            description: <>Whatever you leave unasked dies with her. <strong>Your children will leave yours unasked too.</strong></>
        }
    },
    {
        id: 907,
        component: QUESTION_MULTI_ANSWER_SLIDE_COMPONENT,
        continueButton: {
            isVisible: true,
            minAnswers: 2
        },
        props: {
            category: 'Personalize your plan',
            question: 'What do you want to know about him?',
            description: 'Select at least 2',
            answers: PLAN_TOPIC_ANSWERS
        }
    },
    {
        id: 908,
        component: QUESTION_ANSWER_SLIDE_COMPONENT,
        props: {
            category: 'Personalize your plan',
            question: 'Which chapter should he start with?',
            answerSourceId: 907,
            answerPool: PLAN_TOPIC_ANSWERS
        }
    },
    {
        id: 9081,
        component: PRODUCT_SLIDE,
        isNotIndexing: true,
        continueButton: {
            isVisible: true
        },
        props: {
            cardQuestion: 'What did your father want to be before he had children?',
            titlePrefix: '"There’s nothing to tell"',
            titleAccent: 'is the saddest lie he believes.',
            description: 'Ask broadly, and he’ll insist his dreams didn’t matter. Ask one specific question, and a lifetime unlocks in seconds.'
        }
    },
    {
        id: 909,
        component: QUESTION_ANSWER_SLIDE_COMPONENT,
        props: {
            category: 'Personalize your plan',
            question: 'Which would be easier for him?',
            answers: [
                {
                    text: 'Talking — he’d never sit and write',
                    icon: 'mic-old',
                    id: 4
                },
                {
                    text: 'Writing — he’d want the words right',
                    icon: 'writing',
                    id: 5
                },
                {
                    text: 'Depends on the day',
                    icon: 'question',
                    id: 6
                }
            ]
        }
    },
    {
        id: 9091,
        component: PRODUCT_SLIDE,
        isNotIndexing: true,
        continueButton: {
            isVisible: true
        },
        props: {
            variant: 'v2',
            headingPrefix: 'Whichever he feels like.',
            headingAccent: 'It comes back the same.',
            stepOptions: [
                {
                    icon: 'pencil',
                    title: 'He types a few lines',
                    subtitle: 'whenever he has a minute'
                },
                {
                    icon: 'mic',
                    title: 'He presses record and talks',
                    subtitle: 'we turn it into writing for him'
                }
            ],
            chapterTitle: 'His first chapter',
            chapterBullets: ['edited and polished', 'in his own words', 'ready to share']
        }
    },
    {
        id: 9092,
        component: PLAN_GRAPH_SLIDE,
        isNotIndexing: true,
        continueButton: {
            isVisible: true,
            label: 'Start today'
        },
        props: {
            titlePrefix: 'His story won’t write itself',
            titleAccent: 'before time runs out.',
            topics: ['house', 'backpack', 'bottle', 'groom', 'new-father', 'dad-working', 'tree']
        }
    },
    {
        id: 910,
        component: QUESTION_MULTI_ANSWER_SLIDE_COMPONENT,
        continueButton: {
            isVisible: true
        },
        props: {
            category: 'Personalize your plan',
            question: 'Who else should be able to hear this one day?',
            answers: [
                {
                    text: 'My children',
                    icon: 'children',
                    id: 1
                },
                {
                    text: 'My brothers and sisters',
                    icon: 'siblings',
                    id: 2
                },
                {
                    text: 'Grandchildren who aren’t born yet',
                    icon: 'baby',
                    id: 3
                },
                {
                    text: 'Him — I think he’d like to read it back',
                    icon: 'heart-red',
                    id: 4
                },
                {
                    text: 'Just me',
                    icon: 'smile',
                    id: 5
                }
            ]
        }
    },
    {
        id: 911,
        component: QUESTION_ANSWER_SLIDE_COMPONENT,
        props: {
            category: 'Personalize your plan',
            question: 'What will haunt you when he’s gone?',
            answers: [
                {
                    text: 'His secrets that will die with him',
                    icon: 'candle',
                    id: 7
                },
                {
                    text: 'The stories my children will never get to hear',
                    icon: 'girl',
                    id: 8
                },
                {
                    text: 'Never hearing him say he was proud of me',
                    icon: 'sad',
                    id: 9
                },
                {
                    text: 'That I had the time today — and chose to wait',
                    icon: 'hourglass',
                    id: 10
                },
                {
                    text: 'All of the above',
                    icon: 'broken-heart',
                    id: 11
                }
            ]
        }
    },
    {
        id: 944,
        component: PLAN_LOADER_SLIDE,
        isNotBackButton: true,
        isNotIndexing: true,
        props: {
            steps: ['Reading your answers', 'Preparing his first chapter', 'Writing his letter', 'First question ready'],
            stats: [
                {
                    id: 1,
                    percent: '79%',
                    description: 'of Americans want to know more about their parents’ lives.',
                    highlight: <>Give Dad a place to tell <strong>his story</strong>.</>
                },
                {
                    id: 2,
                    percent: '37%',
                    description: 'know much about their father’s life before they were born.',
                    highlight: <strong>Give him a place to tell it.</strong>
                },
                {
                    id: 3,
                    percent: '53%',
                    description: 'of us can’t name all four of our grandparents.',
                    highlight: <strong>Start with the one you can still ask.</strong>
                }
            ]
        }
    },
    {
        id: 945,
        component: EMAIL_SLIDE,
        isNotBackButton: true,
        isNotIndexing: true,
        props: {
            title: 'Where should we write to him?',
            description: 'Enter his email to create his account and send him an invitation.',
            placeholder: 'Enter his personal email'
        }
    }
];

export {
    QUIZ_SLIDES_V28
};
